/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE "UNSAVED CHANGES" PROMPT IN FRONT OF SAVE-THEN-ACT — ONE COPY DECK
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Product owner, 2026-08-10: *"if there are unsaved changes and the user clicks
 * approve lets have a popup saying 'un saved changes!' then offer buttons like
 * 'save and approve' 'cancel' cancel doesnt undo the changes it just stops the
 * approval"*.
 *
 * 🚨 **THIS IS A DELIBERATE REFINEMENT OF HIS OWN 2026-08-09 RULING, NOT A
 * CONTRADICTION OF IT — AND THAT IS THE SENTENCE THAT STOPS THE NEXT READER
 * "RESTORING" THE SILENT SAVE AS A BUG FIX.** On 2026-08-09 he said *"approve
 * butotn should approve and save the current state"*, and it shipped as a
 * SILENT save-then-act: a dirty form was PUT and the action then ran, with
 * nothing on screen saying a write had happened. The 2026-08-10 message above
 * does not withdraw that — the action still applies to the SAVED order, and a
 * failed save still cannot act. **What changed is that the save is now
 * EXPLICIT**: the user is asked, and they may decline without losing anything.
 * Both quotes are recorded here together because either alone reads as the
 * whole ruling and each would be implemented differently on its own.
 *
 * 🔑 **THE PROMPT SITS IN FRONT OF THE SAVE-THEN-ACT MACHINERY; IT DOES NOT
 * REPLACE IT.** `OrderInlineEditor`'s optional `saveRequestRef` still publishes
 * a `() => Promise<boolean>` that resolves `true` only after a `res.ok` PUT, and
 * each page still reaches its action from inside that ONE `true` branch. A
 * refused PUT — or a form that failed its own `validateForm` and issued no PUT
 * at all — still cannot action the order, structurally, with no `catch` that has
 * to remember. **Never move the action out of that branch to "simplify" the new
 * dialog.**
 *
 * ── WHY A SHARED MODULE FOR THE PER-ACTION STRINGS ───────────────────────
 * 🔻 Was "seven strings" until 2026-08-16 — `partially_delivered` and
 * `delivered` are gone below (redundant with the receive-item ticks); do not
 * re-count from this line, count the object below it.
 * `DepartmentOrders.js` and `Approvals.js` each have their own `saveThenRun`,
 * deliberately (this repo's own rule: *"keep the two identical, not merely
 * similar"*). Two hand-written copies of this deck would drift within a month
 * and the drift would be INVISIBLE — a dialog wording difference between two
 * pages fails no test and looks like a per-page decision. One deck, imported
 * twice.
 *
 * ── COPY DECISIONS, EACH TAKEN AGAINST AN ALTERNATIVE ─────────────────────
 * - **Title `Unsaved changes`, not his literal `un saved changes!`.** Dictation,
 *   and house style is sentence case with no shouting. The MEANING is his,
 *   verbatim.
 * - **The confirm label NAMES THE ACTION** (`Save and approve`, `Save and mark
 *   ordered`, …) rather than a generic `Save and continue`. A dialog that says
 *   what it is about to do is the whole point of asking; "continue" would put
 *   the user back to guessing which button they pressed three seconds ago.
 * - 🔑 **The cancel slot is `Keep editing`, NOT `Cancel`, and this is a
 *   DEPARTURE FROM HIS WORD taken on two grounds.** `client/src/pages/CLAUDE.md`
 *   already rules for this dialog family that *"EVERY BUTTON SAYS WHAT IT DOES,
 *   AND 'CANCEL' APPEARS NOWHERE"*. And the collision here is worse than the one
 *   that rule was written for: **Department Orders' status lifecycle contains a
 *   `cancel` ACTION**, so on that path a `Cancel` button would sit beside
 *   `Save and cancel order` and mean something different from its neighbour.
 *   `Keep editing` is also a literal statement of what he asked for — *"cancel
 *   doesnt undo the changes it just stops the approval"* — i.e. you stay in the
 *   editor with your edits intact. **Flagged to him; it is one token to change
 *   back if he wants his own word.**
 * - **There is NO third button, and its absence is a decision.** `ConfirmDialog`
 *   supports one (`altLabel`/`onAlt`), and "Approve without saving" is the
 *   obvious candidate. It is not offered: he did not ask for it, and actioning a
 *   state DIFFERENT from the one on screen is precisely what the 2026-08-09
 *   ruling exists to prevent. Adding it would undo that ruling through a button.
 *
 * ⚠️ **`Keep editing` TAKES INITIAL FOCUS and Escape / an overlay click both map
 * to it** (`initialFocus="cancel"` at both call sites). It is the only
 * non-committing outcome, so an instinctive Enter or Escape cannot fire an
 * unreviewed save-and-action. Same reasoning as the collapse prompt's.
 *
 * 🔴 **NO `variant="danger"`.** Every branch of this dialog is recoverable —
 * nothing here discards an edit — so red would be crying wolf on a prompt the
 * user will see often.
 */

/**
 * Keyed by the ACTION STRING each page passes to its `saveThenRun`.
 *
 * 🚨 **THE KEY SET IS A CONTRACT, PINNED BY
 * `client/src/constants/saveThenActPrompt.test.js`.** A mutating action added to
 * `runRowAction` without an entry here would silently fall to
 * `SAVE_THEN_ACT_FALLBACK` and ship a dialog reading "Save and continue" —
 * correct, harmless-looking, and wrong, which is the shape nobody reports. The
 * test asserts this object's keys EQUAL the set of non-navigating actions the
 * two pages actually dispatch, so adding an action without its copy goes red.
 *
 * `gerund` is dropped into: *"This order has edits that have not been saved.
 * ${gerund} will save them first, so it applies to the order as you see it
 * now."* — so it must read as the subject of "will save".
 */
export const SAVE_THEN_ACT_PROMPT = Object.freeze({
  approve: Object.freeze({
    confirmLabel: 'Save and approve',
    gerund: 'Approving',
  }),
  reject: Object.freeze({
    confirmLabel: 'Save and reject',
    gerund: 'Rejecting',
  }),
  ordered: Object.freeze({
    confirmLabel: 'Save and mark ordered',
    gerund: 'Marking this order as ordered',
  }),
  // 🔀 2026-09-13 (Pete) — "Mark all Delivered" is BACK as a Next-step menu
  // item, this time as a one-click shortcut onto the tick cluster's own
  // `receivedHandlers.receiveAll` (`PATCH …/items/receive-all`), never the
  // status override and never `partially_delivered`/`delivered` above (those
  // stay deleted). The `kind` string is `receive_all` (underscore, not a
  // hyphen) so this file's OWN source scan — `[a-z_]+` — can see it; see
  // `DepartmentOrders.js`'s `receive-all` menu item for why the two spellings
  // differ (the item `key`/testid keep the hyphen everything else here uses).
  receive_all: Object.freeze({
    confirmLabel: 'Save and mark all delivered',
    gerund: 'Marking every line item on this order as delivered',
  }),
  // 🔻 **`partially_delivered` AND `delivered` WERE HERE AND ARE GONE
  // (2026-08-16), SAME SHAPE AS `restore` BELOW.** They read
  // `{ confirmLabel: 'Save and mark partially delivered', gerund: 'Recording
  // a partial delivery' }` and `{ confirmLabel: 'Save and mark delivered',
  // gerund: 'Marking this order as delivered' }`. Owner: *"Also remove the
  // discussed items from the action menu"* — Partial Delivery + Mark
  // Delivered, both a manual `PATCH /:id/status` made redundant by the
  // receive-item endpoints, which already recompute this exact status from
  // the line-item ticks. `DepartmentOrders.js` no longer dispatches either
  // action, so §1 of this file's test requires the deck's key set to EQUAL
  // the live set. **Do not re-add either entry without re-adding the
  // action** — an orphan key is exactly what that assertion exists to catch.
  // 🔀 2026-09-13 (Pete) — the approval OVERRIDE, rebuilt as the `Next step`
  // menu item `Skip remaining approvals…` on Department Orders. It is a
  // mutating action dispatched through `saveThenRun`, so §1 of this file's test
  // requires an entry; without one the dialog would read "Save and continue".
  // 🔑 The confirm label says SKIP, matching the menu item's own verb — the
  // user is three seconds from a second dialog that asks WHY, and the two must
  // obviously be about the same act. "Save and override" would name the route
  // instead of the action, which is the thing the item's label deliberately
  // stopped doing.
  override: Object.freeze({
    confirmLabel: 'Save and skip approvals',
    gerund: 'Skipping the remaining approvals',
  }),
  // 🪤 THE ONE THAT FORCED `Keep editing`. `Cancel` in the cancel slot beside
  // `Save and cancel order` would be two buttons using one word for two
  // different things, on the most destructive item in the lifecycle. Reached
  // only through the status override's `cancelled` option — the standalone
  // Cancel button was removed 2026-05-28.
  cancel: Object.freeze({
    confirmLabel: 'Save and cancel order',
    gerund: 'Cancelling this order',
  }),
  // 🔻 **`restore` WAS HERE AND IS GONE (2026-08-12).** It read
  // `{ confirmLabel: 'Save and restore', gerund: 'Restoring this order' }`.
  // The `Restore` control is deleted — leaving `cancelled` is the status
  // override's job now — so no page dispatches a `restore` action and §1 of this
  // file's test requires the deck's key set to EQUAL the live set. **Do not
  // re-add the entry without re-adding the action**; an orphan key is exactly
  // what that assertion exists to catch.
});

/**
 * Reached only by an action with no entry above — which the test forbids, so in
 * a healthy tree this is unreachable. It exists so that a future action cannot
 * render `undefined` into a button label; it is a seatbelt, not a default to
 * design against.
 */
export const SAVE_THEN_ACT_FALLBACK = Object.freeze({
  confirmLabel: 'Save and continue',
  gerund: 'Continuing',
});

export const SAVE_THEN_ACT_TITLE = 'Unsaved changes';
export const SAVE_THEN_ACT_KEEP_EDITING = 'Keep editing';

export function saveThenActCopy(kind) {
  const entry = SAVE_THEN_ACT_PROMPT[kind] || SAVE_THEN_ACT_FALLBACK;
  return {
    confirmLabel: entry.confirmLabel,
    message:
      'This order has edits that have not been saved. ' +
      `${entry.gerund} will save them first, so it applies to the order as you see it now.`,
  };
}
